import type { AIModel } from '@/api/types'
import { ensureAudioNodeData } from '@/features/studio/canvas/audio-tabs'
import { mergeGenParams } from '@/features/studio/canvas/model-params'
import {
  migrateNode,
  refreshUpstreamPreviews,
} from '@/features/studio/canvas/utils'
import {
  taskTypeForNode,
  type StudioFlowEdge,
  type StudioFlowNode,
} from '@/features/studio/canvas/types'

export type ServerGraphInput = {
  nodes?: unknown[]
  edges?: unknown[]
  viewport?: { x?: number; y?: number; zoom?: number }
}

export function ensureNodeModelDefaults(
  node: StudioFlowNode,
  defaultModelId: (type: 'image' | 'video' | 'audio' | 'text') => string,
  audioModels: AIModel[] = [],
): StudioFlowNode {
  if (node.data.nodeType === 'gen_audio') {
    const data = ensureAudioNodeData(node.data, audioModels)
    if (data === node.data) return node
    return { ...node, data }
  }
  if (node.data.nodeType === 'text') {
    return {
      ...node,
      data: {
        ...node.data,
        gen: {
          modelId: node.data.gen?.modelId || defaultModelId('text'),
          params: node.data.gen?.params ?? {},
        },
      },
    }
  }
  const taskType = taskTypeForNode(node.data.nodeType)
  if (!taskType) return node
  return {
    ...node,
    data: {
      ...node.data,
      gen: {
        modelId: node.data.gen?.modelId || defaultModelId(taskType),
        params: mergeGenParams(node.data.nodeType, node.data.gen?.params),
      },
    },
  }
}

export function prepareStudioGraphFromServer(
  graph: ServerGraphInput | undefined,
  opts: {
    defaultModelId: (type: 'image' | 'video' | 'audio' | 'text') => string
    audioModels: AIModel[]
  },
): {
  nodes: StudioFlowNode[]
  edges: StudioFlowEdge[]
  viewport?: { x: number; y: number; zoom: number }
} {
  let initialNodes = ((graph?.nodes ?? []) as StudioFlowNode[]).map((n) => migrateNode(n))
  const initialEdges = ((graph?.edges ?? []) as StudioFlowEdge[]).map((e) => ({
    ...e,
    type: 'studio' as const,
    data: { animated: true, ...e.data },
  }))
  initialNodes = initialNodes.map((n) => ensureNodeModelDefaults(n, opts.defaultModelId, opts.audioModels))
  initialNodes = refreshUpstreamPreviews(initialNodes, initialEdges)
  const vp = graph?.viewport
  const viewport =
    vp && typeof vp.x === 'number' && typeof vp.y === 'number'
      ? { x: vp.x, y: vp.y, zoom: typeof vp.zoom === 'number' ? vp.zoom : 1 }
      : undefined
  return { nodes: initialNodes, edges: initialEdges, viewport }
}
